/* Built In Imports */
import { useBreakpointValue } from '@chakra-ui/react';
/* External Imports */
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Button, Center, Flex, Text } from '@chakra-ui/react';
import { nanoid } from 'nanoid';
// import { FaLinkedin } from 'react-icons/fa';
import config from '@config';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
/* Internal Imports */
/* Components */
import SectionTitle from '@components/HNI/Components/Headings/SectionTitle';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
// import TestimonialSt from '@shi/styleData/TestimonialSt';
/* Services */
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';

/**
 * Renders the Testimonials component
 *
 * @param {object} sectionContent - Data for section
 * @param {string} region - Region of the section
 * @param {string} lang - Language of the section
 * @returns {ReactElement} Testimonials component
 */

const TestimonialMaha = ({ sectionContent, region, lang }) => {
  const testimonialData = sectionContent;
  const currentBreakpoint = useBreakpointValue({
    base: 'base',
    md: 'md',
    lg: 'lg',
  });
  const slidesCount = testimonialData?.[0]?.testimonials?.length || 0;
  // Determine if navigation icons should be shown
  const showIcons = (() => {
    if (currentBreakpoint === 'base' || currentBreakpoint === 'md') {
      // Show icons for base and md if there are at least 2 slides
      return slidesCount > 1;
    } else if (currentBreakpoint === 'lg') {
      // Show icons for lg and above only if there are more than 2 slides
      return slidesCount > 2;
    }
    return false;
  })();
  const pagination = {
    clickable: true,
    renderBullet: function (index, className) {
      return '<span class="' + className + '">' + '</span>';
    },
  };
  const iconSize = { base: '25px', md: '30px' };

  return (
    <>
      <Box
        pt={{ base: '1.8rem', md: '2.5rem', lg: '3rem' }}
        pb={{ base: '2rem' }}
      >
        <Box textAlign={'center'}>
          <SectionTitle
            titleObj={testimonialData[0].title[0]}
            fontSize={{ base: '26px', md: '30px' }}
            lineHeight={{ base: '47px' }}
          />
        </Box>
        <Box
          width="100%"
          p={{ base: '30px 20px', md: '30px 20px' }}
          maxW={{ base: '600px', lg: '900px', xl: '1050px' }}
          mx="auto"
          display="flex"
        >
          <Box w="100%" pos="relative">
            <Swiper
              slidesPerView={1}
              spaceBetween={30}
              pagination={pagination}
              breakpoints={{
                960: {
                  slidesPerView: 2,
                  spaceBetween: 30,
                },
              }}
              autoplay={{
                delay: 3000,
                disableOnInteraction: true,
              }}
              // lazy={true}
              loop={true}
              modules={[Pagination, Navigation, Autoplay]}
              navigation={{ nextEl: '.test-right', prevEl: '.test-left' }}
              className="testSwiper"
            >
              {testimonialData?.[0]?.testimonials?.map((el, i) => {
                return (
                  <SwiperSlide key={nanoid()}>
                    <Center
                      // height={{ base: '435px', sm: '100%' }}
                      // minH={{ base:'415px', sm:'355px'}}
                      background="#FFF9EA"
                      display="flex"
                      w="100%"
                      flexDirection={{ base: 'column', md: 'row' }}
                      alignSelf={{ base: 'center', sm: 'unset' }}
                      key={i}
                      pos="relative"
                      px={{ base: '2.5rem', md: '4rem', xl: '6rem' }}
                      // pt={{
                      //   base: '4rem',
                      //   md: '6rem',
                      //   xl: '3rem',
                      //   '2xl': '4rem',
                      // }}
                      // pb={{ base: '2rem' }}
                      textAlign="center"
                      mb={{ base: '50px', md: '70px' }}
                      rounded="12px"
                    >
                      <Box
                        display="flex"
                        justifyContent={'center'}
                        pos="absolute"
                        bottom={{ base: '-35px', md: '-45px' }}
                        left="0"
                        right="0"
                      >
                        <LazyLoadImageComponent
                          loading="lazy"
                          src={el?.photo?.url}
                          title={el?.photo?.title}
                          alt={el?.photo?.alt || ''}
                          borderRadius="50%"
                          width={{ base: 70, md: 90 }}
                          height={{ base: 70, md: 90 }}
                          background="lightgray 50% / cover no-repeat"
                          border="3px solid #B56576"
                        />
                      </Box>
                      <Box display="block" m={{ base: '50px 10px', md: '50px 0' }}>
                        <Text
                          pos="relative"
                          fontSize="1rem"
                          lineHeight="28px"
                          color="#28231E"
                          fontFamily="FedraSansStd-book"
                          _before={{
                            content: "''",
                            width: { base: '20px', md: '33px' },
                            height: { base: '20px', md: '30px' },
                            bgPos: 'center',
                            bgSize: 'cover',
                            bgRepeat: 'no-repeat',
                            bgImage: `${config.imgPath}/d/46272/1732797436-leftquote.png`,
                            pos: 'absolute',
                            zIndex: 1,
                            left: { base: '-20px', md: '-35px', lg: '-45px' },
                            top: {
                              base: '-30px',
                              sm: '-30px',
                              md: '-35px',
                              lg: '-30px',
                            },
                          }}
                          maxW={{ base: 205, sm: 325 }}
                          _after={{
                            content: "''",
                            bgImage: `${config.imgPath}/d/46272/1732797436-rightquote.png`,
                            width: { base: '20px', md: '33px' },
                            height: { base: '20px', md: '30px' },
                            bgPos: 'center',
                            bgSize: 'cover',
                            bgRepeat: 'no-repeat',
                            pos: 'absolute',
                            zIndex: 1,
                            right: { base: '-25px', md: '-1rem', lg: '-30px' },
                            bottom: { base: '-20%', md: '-3rem', lg: '0' },
                          }}
                        >
                          {el?.testimonialText}
                        </Text>
                        <Text color="#28231E" mt="40px">
                          <Text
                            fontSize={{ base: '18px', md: '22px' }}
                            lineHeight="28px"
                            fontFamily="FedraSansStd-medium"
                            // {...TestimonialSt[lang].testimonialName}
                          >
                            {el?.userName}
                          </Text>
                          <Text
                            as="span"
                            color="#28231E"
                            fontSize="16px"
                            fontFamily="FedraSansStd-book"
                            lineHeight="28px"
                            fontWeight="450"
                            // {...TestimonialSt[lang].testimonialDes}
                          >
                            {el?.userDescription}
                          </Text>
                        </Text>
                      </Box>
                    </Center>
                  </SwiperSlide>
                );
              })}
            </Swiper>
            {/* <Hide below="md"> */}
            {/* { && ( */}
            <Flex
              pos="absolute"
              display={showIcons ? 'flex' : 'none'}
              width={{ base: '96%', md: '100%' }}
              maxW="100%"
              // overflow={{ base: 'hidden', md: 'visible' }}
              top={{ base: '30%', md: '33%' }}
              left={{ base: '2%', md: '0%' }}
              zIndex="4"
              justifyContent="space-between"
              flexDir="row-reverse"
              // transform="translate(-50%, -50%)"
            >
              <Button
                className="test-right arrow"
                height={{ base: '40px', md: '60px' }}
                width={{ base: '40px', md: '60px' }}
                mr={{ base: '-20px', md: '-20px' }}
                _focus={{ boxShadow: 'none' }}
                color="#000"
                background="#fff"
                _hover={{
                  color: '#FFFFFF',
                  bgColor: '#D04723',
                }}
                border="none"
                boxShadow="xl"
                display="flex"
                p="0"
                rounded="full"
                value="right-arrow"
              >
                <ChevronRightIcon fontSize={iconSize} fontWeight="bold" />
              </Button>
              <Button
                className="test-left arrow"
                display="flex"
                height={{ base: '40px', md: '60px' }}
                width={{ base: '40px', md: '60px' }}
                ml={{ base: '-20px', md: '-20px' }}
                _focus={{ boxShadow: 'none' }}
                color="#000"
                background="#fff"
                value="left-arrow"
                _hover={{
                  color: '#FFFFFF',
                  bgColor: '#D04723',
                }}
                border="none"
                boxShadow="xl"
                p="0"
                rounded="full"
              >
                <ChevronLeftIcon fontSize={iconSize} fontWeight="bold" />
              </Button>
            </Flex>
            {/* )} */}
          </Box>
        </Box>
        <style>
          {`
            .swiper{
              padding-bottom:40px;
            }
            .swiper-pagination-fraction, .swiper-pagination-custom, .swiper-horizontal > .swiper-pagination-bullets, .swiper-pagination-bullets.swiper-pagination-horizontal{
              bottom:0px;
            }
            .swiper-wrapper {
              justify-content: center;
            }
            .swiper-pagination-bullet {
              width: 10px;
              height: 10px;
              color: #000;
              opacity: 1;
              background: #E4DED2;
            }
             .swiper-pagination-bullet-active {
              color: #fff;
              background: #BB9B4B;
            }
            .swiper-button-disabled{
              visibility: hidden;
            }
            `}
        </style>
      </Box>
    </>
  );
};

export default TestimonialMaha;
